import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
import { onWidthResize, offWidthResize } from '../utils/resize.js';
gsap.registerPlugin(ScrollTrigger, SplitText);

const THUMB_COUNT = 12;

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60).toString().padStart(2, '0');
  const s = (total % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

export default class AboutHero {
  constructor(container) {
    this.container = container;
    this.title = container.querySelector('.about-hero__title');
    this.description = container.querySelector('.about-hero__description');
    this.top = container.querySelector('.about-hero__top');
    this.photo = container.querySelector('.about-hero__photo');
    this.trigger = container.querySelector('[data-video-trigger]');
    this.modal = container.querySelector('[data-video-modal]');
    this.mm = gsap.matchMedia();

    this.initTitleAnimation();
    this.initDescriptionAnimation();
    this.initPhotoAnimation();
    this.initPin();
    this.initCursor();

    if (!this.trigger || !this.modal) return;

    this.video = this.modal.querySelector('[data-video-element]');
    this.controls = this.modal.querySelector('[data-video-controls]');
    this.playBtn = this.modal.querySelector('[data-video-play]');
    this.currentLabel = this.modal.querySelector('[data-video-current]');
    this.totalLabel = this.modal.querySelector('[data-video-total]');
    this.range = this.modal.querySelector('[data-video-range]');
    this.progress = this.modal.querySelector('[data-video-progress]');
    this.thumbStrip = this.modal.querySelector('[data-video-thumbs]');
    this.volumeBtn = this.modal.querySelector('[data-video-volume]');
    this.fullscreenBtn = this.modal.querySelector('[data-video-fullscreen]');
    this.closeBtn = this.modal.querySelector('[data-video-close]');

    this.thumbnailsReady = false;
    this.bind();


  }

  initCursor() {
    this.cursor = this.container.querySelector('.about-hero__cursor')

    gsap.set(this.cursor, { xPercent: -50, yPercent: -50, opacity: 0, scale: 0.8 });

    this.xTo = gsap.quickTo(this.cursor, 'x', { duration: 0.5, ease: 'power3.out' });
    this.yTo = gsap.quickTo(this.cursor, 'y', { duration: 0.5, ease: 'power3.out' });

    gsap.to(this.cursor.querySelector('.svg'), { rotation: 360, duration: 10, repeat: -1, ease: 'none', });

    this.photo.addEventListener('mouseenter', this.onCursorEnter);
    this.photo.addEventListener('mouseleave', this.onCursorLeave);
    window.addEventListener('mousemove', this.onCursorMove);
  }

  onCursorEnter = () => {
    this.photo.classList.add('section-about-hero--cursor');
    gsap.to(this.cursor, { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' });
  };

  onCursorLeave = () => {
    this.photo.classList.remove('section-about-hero--cursor');
    gsap.to(this.cursor, { opacity: 0, scale: 0.8, duration: 0.3, ease: 'power2.in' });
  };

  onCursorMove = (e) => {
    this.xTo(e.clientX);
    this.yTo(e.clientY);
  };

  initPin() {
    if (!this.top) return;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    let tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.container,
        start: isMobile ? 'top top' : 'top top',
        end: isMobile ? '250px' : '400px',
        pin: this.top,
        pinSpacing: false,
        // markers: true,
        scrub: true
      }
    });

    tl.to(this.titleSplit.words, { opacity: 0, duration: 1, stagger: { each: 0.05, from: 'end' }, ease: 'power2.out', }, 0);
    tl.to(this.description, { opacity: 0, ease: 'power2.out', }, 0.4);
    tl.to(this.top, { scale: 0.99, duration: 1, }, 0);
  }

  initPhotoAnimation() {
    if (!this.photo) return;
    gsap.fromTo(
      this.photo,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        delay: 1.4,
        ease: 'power3.out',
        scrollTrigger: { trigger: this.photo },
      }
    );
  }

  initTitleAnimation() {
    if (!this.title) return;
    this.titleSplit = new SplitText(this.title, { type: 'lines,words,chars', linesClass: 'lineParent' });
    gsap.fromTo(
      this.titleSplit.words,
      { yPercent: 100 },
      {
        yPercent: 0,
        duration: 0.8,
        delay: 1,
        stagger: { each: 0.08, ease: 'power2.out', from: 'start' },
        ease: 'power3.out',
        scrollTrigger: { trigger: this.container },
        onComplete: () => {
          //  onWidthResize(this.onTitleResize);
        },
      }
    );
  }

  onTitleResize = () => {
    this.titleSplit?.revert();
    offWidthResize(this.onTitleResize);
  };

  initDescriptionAnimation() {
    if (!this.description) return;
    new SplitText(this.description, { type: 'lines', linesClass: 'lineParent' });
    this.descSplit = new SplitText(this.description.querySelectorAll('.lineParent'), { type: 'lines', linesClass: '' });
    gsap.fromTo(
      this.descSplit.lines,
      { yPercent: 100 },
      {
        yPercent: 0,
        duration: 0.8,
        stagger: { each: 0.1, ease: 'power2.out', from: 'start' },
        ease: 'power3.out',
        delay: 1.2,
        scrollTrigger: { trigger: this.container },
        onComplete: () => {
          onWidthResize(this.onDescResize);
        },
      }
    );
  }

  onDescResize = () => {
    this.descSplit?.revert();
    offWidthResize(this.onDescResize);
  };

  bind() {
    this.trigger.addEventListener('click', this.open);
    this.closeBtn?.addEventListener('click', this.close);
    this.modal.addEventListener('cancel', this.close);

    this.playBtn?.addEventListener('click', this.togglePlay);
    this.video?.addEventListener('click', this.togglePlay);
    this.video?.addEventListener('play', this.onPlay);
    this.video?.addEventListener('pause', this.onPause);
    this.video?.addEventListener('timeupdate', this.onTimeUpdate);
    this.video?.addEventListener('loadedmetadata', this.onLoadedMetadata);
    this.video?.addEventListener('ended', this.onPause);

    this.range?.addEventListener('input', this.onSeek);
    this.volumeBtn?.addEventListener('click', this.toggleMute);
    this.fullscreenBtn?.addEventListener('click', this.toggleFullscreen);

    if (this.video && this.video.readyState >= 1) this.onLoadedMetadata();
  }

  open = () => {
    if (!this.modal || !this.video) return;
    if (typeof this.modal.showModal === 'function') {
      this.modal.showModal();
    } else {
      this.modal.setAttribute('open', '');
    }
    this.openTween?.kill();
    this.openTween = gsap.fromTo(
      this.modal,
      { clipPath: 'inset(100% 0% 0% 0%)' },
      { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, ease: 'expo.inOut' }
    );
    window.lenis?.stop?.();
    setTimeout(() => {
      this.video.play().catch(() => { });
      if (!this.thumbnailsReady && this.video.readyState >= 1) {
        this.generateThumbnails();
      }
    }, 500);

  };

  close = (event) => {
    event?.preventDefault?.();
    this.openTween?.kill();
    gsap.set(this.modal, { clearProps: 'clipPath' });
    this.video?.pause();
    if (this.video) this.video.currentTime = 0;
    if (typeof this.modal.close === 'function') {
      try { this.modal.close(); } catch (_) { this.modal.removeAttribute('open'); }
    } else {
      this.modal.removeAttribute('open');
    }
    window.lenis?.start?.();
  };

  togglePlay = () => {
    if (!this.video) return;
    if (this.video.paused || this.video.ended) {
      this.video.play().catch(() => { });
    } else {
      this.video.pause();
    }
  };

  onPlay = () => {
    this.modal.dataset.state = 'playing';
  };

  onPause = () => {
    this.modal.dataset.state = 'paused';
  };

  onTimeUpdate = () => {
    const { currentTime, duration } = this.video;
    if (this.currentLabel) this.currentLabel.textContent = formatTime(currentTime);
    if (!Number.isFinite(duration) || duration <= 0) return;
    const pct = currentTime / duration;
    if (this.range && document.activeElement !== this.range) {
      this.range.value = String(Math.round(pct * 1000));
    }
    if (this.progress) this.progress.style.width = `${pct * 100}%`;
  };

  onLoadedMetadata = () => {
    const { duration } = this.video;
    if (this.totalLabel) this.totalLabel.textContent = formatTime(duration);
    if (this.modal.open && !this.thumbnailsReady) this.generateThumbnails();
  };

  onSeek = () => {
    const { duration } = this.video;
    if (!Number.isFinite(duration) || duration <= 0) return;
    const pct = Number(this.range.value) / 1000;
    this.video.currentTime = pct * duration;
  };

  toggleMute = () => {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.modal.dataset.muted = String(this.video.muted);
  };

  toggleFullscreen = () => {
    if (!this.video) return;

    const fsElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.webkitCurrentFullScreenElement;

    if (fsElement) {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.webkitCancelFullScreen;
      exit?.call(document);
      return;
    }

    // Prefer fullscreen on the <video> itself — universal support, including iOS Safari
    // which only allows fullscreen on the video element via webkitEnterFullscreen.
    const enter =
      this.video.requestFullscreen ||
      this.video.webkitRequestFullscreen ||
      this.video.webkitEnterFullscreen ||
      this.modal.requestFullscreen ||
      this.modal.webkitRequestFullscreen;

    const target = (this.video.requestFullscreen || this.video.webkitRequestFullscreen || this.video.webkitEnterFullscreen)
      ? this.video
      : this.modal;

    try {
      const result = enter?.call(target);
      if (result && typeof result.catch === 'function') {
        result.catch(() => { });
      }
    } catch (_) {
      /* fullscreen blocked or unsupported */
    }
  };

  async generateThumbnails() {
    if (!this.thumbStrip || !this.video) return;
    const source = this.video.currentSrc || this.video.querySelector('source')?.src;
    if (!source) return;

    this.thumbnailsReady = true;

    const probe = document.createElement('video');
    probe.crossOrigin = 'anonymous';
    probe.preload = 'auto';
    probe.muted = true;
    probe.playsInline = true;
    probe.src = source;

    try {
      await new Promise((resolve, reject) => {
        probe.addEventListener('loadedmetadata', resolve, { once: true });
        probe.addEventListener('error', reject, { once: true });
      });

      const duration = probe.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;

      const canvas = document.createElement('canvas');
      const w = probe.videoWidth || 160;
      const h = probe.videoHeight || 90;
      const scale = 160 / w;
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');

      this.thumbStrip.replaceChildren();

      for (let i = 0; i < THUMB_COUNT; i += 1) {
        const t = (duration * (i + 0.5)) / THUMB_COUNT;
        // eslint-disable-next-line no-await-in-loop
        await this.seekTo(probe, t);
        ctx.drawImage(probe, 0, 0, canvas.width, canvas.height);
        const cell = document.createElement('div');
        cell.className = 'video-modal__thumb';
        cell.style.backgroundImage = `url(${canvas.toDataURL('image/jpeg', 0.7)})`;
        this.thumbStrip.appendChild(cell);
      }
    } catch (_) {
      this.thumbnailsReady = false;
    }
  }

  seekTo(video, time) {
    return new Promise((resolve) => {
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        resolve();
      };
      video.addEventListener('seeked', onSeeked);
      try {
        video.currentTime = time;
      } catch (_) {
        video.removeEventListener('seeked', onSeeked);
        resolve();
      }
    });
  }
}
